import type { DestinationDef } from '@/lib/country-month-pages';
import { SectionShell } from '@/components/layout/SectionShell';
import { getCountryMonthData, MONTH_DISPLAY } from '@/lib/country-month-pages';
import { getCountryByCode, getCountries, getDestinationMonth } from '@/lib/data-source';
import { isPublicHoliday } from '@/lib/event-filters';
import { getOriginVacationWindows } from '@/lib/cross-calendar';
import { CrossCalendarPillLinks } from './CrossCalendarPillLinks';
import { WeatherConditionsCard } from './WeatherConditionsCard';
import { VacationWindowsList } from './VacationWindowsList';
import { MonthNavigation } from './MonthNavigation';
import { getTypeLabel } from '@/lib/constants';
import { buildCountryMonthJsonLd, buildTouristDestinationJsonLd, buildArticleJsonLd } from '@/lib/seo';
import { SITE_LAUNCH_DATE } from '@/lib/seo/constants';
import { SITE_URL } from '@/lib/brand';
import { toAbsoluteImageUrl, getCountryOgImage } from '@/lib/images';
import { JsonLdScript } from '@/components/lib/JsonLdScript';
import { FaqBlock } from './FaqBlock';
import { EventListItem } from './EventListItem';
import { CALENDAR_YEAR } from '@/lib/config';
import { uiFb, getTemplateStr } from '@/lib/i18n';
import { Flag } from '@/components/ui/Flag';
import type { EvergreenSlugEntry } from '@/lib/i18n';

const YEAR = CALENDAR_YEAR;

interface CountryMonthSeoSectionProps {
  dest: DestinationDef;
  month: number;
  entry: EvergreenSlugEntry;
}

export function CountryMonthSeoSection({ dest, month, entry }: CountryMonthSeoSectionProps) {
  const lang = entry.lang ?? 'en';
  const country = getCountryByCode(dest.countryCode);
  const countryName = country?.name ?? dest.countryCode;
  const monthName = MONTH_DISPLAY[month - 1];

  const data = getCountryMonthData(dest, month);
  const events = data.events.sort((a, b) => a.startMs - b.startMs);

  const publicHolidays = events.filter(isPublicHoliday);
  const festivals = events.filter(e => e.type === 'cultural_festival');

  // Destination month structured data
  const destMonth = getDestinationMonth(dest.countryCode, month);

  // FAQ — enriched with destination month data when available
  const faqs: Array<{ question: string; answer: string }> = [
    {
      question: getTemplateStr('seo.cm.faq1_q', lang, { destination: dest.displayName, month: monthName }),
      answer: destMonth
        ? getTemplateStr('seo.cm.faq1_a_dm', lang, { month: monthName, destination: dest.displayName, season: destMonth.seasonName, temp: destMonth.tempRange, summary: destMonth.weatherSummary, count: String(events.length) })
        : data.seasonInfo
          ? getTemplateStr('seo.cm.faq1_a_season', lang, { month: monthName, destination: dest.displayName, season: data.seasonInfo, count: String(events.length) })
          : getTemplateStr('seo.cm.faq1_a_basic', lang, { month: monthName, destination: dest.displayName, count: String(events.length) }),
    },
    {
      question: getTemplateStr('seo.cm.faq2_q', lang, { month: monthName, destination: dest.displayName }),
      answer: destMonth
        ? getTemplateStr('seo.cm.faq2_a_dm', lang, { verdict: destMonth.visitVerdict, weather: String(destMonth.weatherScore), crowds: destMonth.crowdLevel, prices: destMonth.priceLevel })
        : festivals.length > 0
          ? `${monthName} offers ${festivals.length} cultural festival${festivals.length !== 1 ? 's' : ''} in ${dest.displayName}, making it a great time to experience local culture.${data.seasonInfo ? ` It falls during the ${data.seasonInfo}.` : ''}`
          : `${monthName} in ${dest.displayName} has ${events.length} events.${data.seasonInfo ? ` The weather corresponds to the ${data.seasonInfo}.` : ''} Check the events below to plan your trip.`,
    },
    {
      question: getTemplateStr('seo.cm.faq3_q', lang, { month: monthName, destination: dest.displayName }),
      answer: destMonth
        ? (() => {
            const key = destMonth.crowdLevel === 'high' ? 'seo.cm.faq3_a_high' : destMonth.crowdLevel === 'medium' ? 'seo.cm.faq3_a_medium' : 'seo.cm.faq3_a_low';
            return getTemplateStr(key, lang, { month: monthName, crowds: destMonth.crowdLevel, prices: destMonth.priceLevel, destination: dest.displayName });
          })()
        : `${dest.displayName} in ${monthName} has ${events.length} events. ${data.seasonInfo ? `The ${data.seasonInfo} influences visitor numbers.` : 'Check local conditions for crowd levels.'}`,
    },
    {
      question: getTemplateStr('seo.cm.faq4_q', lang, { destination: dest.displayName, month: monthName, year: String(YEAR) }),
      answer: publicHolidays.length > 0
        ? getTemplateStr('seo.cm.faq4_a', lang, { destination: dest.displayName, month: monthName, year: String(YEAR), holidays: publicHolidays.map(e => e.title).join(', ') })
        : getTemplateStr('seo.cm.faq4_a_none', lang, { destination: dest.displayName, month: monthName, year: String(YEAR), count: String(events.length) }),
    },
    {
      question: getTemplateStr('seo.cm.faq5_q', lang, { month: monthName }),
      answer: (() => {
        const countriesWithHolidays = getCountries()
          .filter(c => {
            const vw = getOriginVacationWindows(c.code, month);
            return vw.holidays.length > 0;
          })
          .map(c => c.name);
        return countriesWithHolidays.length > 0
          ? getTemplateStr('seo.cm.faq5_a', lang, { month: monthName, countries: countriesWithHolidays.join(', '), destination: dest.displayName })
          : getTemplateStr('seo.cm.faq5_a_fallback', lang, { month: monthName });
      })(),
    },
  ];

  // JSON-LD
  const collectionJsonLd = buildCountryMonthJsonLd(dest.displayName, monthName, events.length, entry.slug, YEAR);
  const touristDestJsonLd = destMonth
    ? buildTouristDestinationJsonLd(dest.displayName, monthName, entry.slug, destMonth.weatherSummary, destMonth.tempRange, dest.countryCode)
    : null;
  const articleJsonLd = buildArticleJsonLd({
    headline: entry.h2Title,
    url: `${SITE_URL}/${entry.slug}`,
    image: toAbsoluteImageUrl(getCountryOgImage({ code: dest.countryCode })),
    datePublished: SITE_LAUNCH_DATE,
    dateModified: new Date().toISOString().slice(0, 10),
    lang,
    articleSection: 'Travel',
  });

  return (
    <SectionShell as="section" className="pb-8 pt-12">
      {/* JSON-LD */}
      <JsonLdScript data={collectionJsonLd} />
      <JsonLdScript data={touristDestJsonLd} />
      <JsonLdScript data={articleJsonLd} />

      {/* Header */}
      <div className="mb-8">
        <h1 className="font-heading text-2xl font-bold text-text-primary md:text-3xl">
          {entry.h2Title}
        </h1>
        <p className="mt-2 text-sm text-text-secondary md:text-base">
          <Flag code={dest.countryCode} size={16} /> {uiFb('ui.events_in_country', `${events.length} events in ${countryName}`, lang, { count: events.length, country: countryName })}
        </p>
        <p className="mt-3 text-sm leading-relaxed text-text-secondary md:text-base">
          {getTemplateStr('seo.cm.intro', lang, { country: countryName, count: String(events.length), month: monthName, year: String(YEAR) })}
          {publicHolidays.length > 0
            ? getTemplateStr('seo.cm.intro_pub', lang, { count: String(publicHolidays.length), holidays: publicHolidays.map(e => e.title).join(', ') })
            : ''}
          {festivals.length > 0
            ? getTemplateStr('seo.cm.intro_fest', lang, { count: String(festivals.length), festivals: festivals.map(e => e.title).join(', ') })
            : ''}
          {data.seasonInfo ? getTemplateStr('seo.cm.intro_season', lang, { season: data.seasonInfo }) : ''}
        </p>
      </div>

      {/* Weather & Conditions */}
      <WeatherConditionsCard destMonth={destMonth} monthName={monthName} countryName={countryName} seasonInfo={data.seasonInfo} lang={lang} />

      {/* Events list */}
      <div className="mb-8 border-t border-border-subtle pt-6">
        <h2 className="font-heading text-xl font-bold text-text-primary md:text-2xl">
          {uiFb('ui.events_holidays_month', 'Events & Holidays This Month', lang)}
        </h2>
        <div className="mt-4 space-y-2">
          {events.map(event => (
            <EventListItem key={event.id} event={event} lang={lang}>
              {event.isGazetted && (
                <span className="badge-success ml-2 rounded px-1.5 py-0.5 text-caption font-medium">
                  {uiFb('ui.holiday', 'Holiday', lang)}
                </span>
              )}
            </EventListItem>
          ))}
        </div>
      </div>

      {/* When Can You Go? — Origin vacation windows */}
      <VacationWindowsList destCountryCode={dest.countryCode} month={month} monthName={monthName} lang={lang} />

      {/* Month navigation */}
      <MonthNavigation month={month} slugPrefix={dest.slugPrefix} />

      {/* Cross-calendar links */}
      <CrossCalendarPillLinks
        slugPrefix={dest.slugPrefix}
        countryCode={dest.countryCode}
        displayName={dest.displayName}
        lang={lang}
        linkTemplateKey="seo.cm.origin_holidays_link"
      />

      {/* FAQ */}
      <FaqBlock faqs={faqs} heading={uiFb('ui.faq_heading', 'Frequently Asked Questions', lang)} className="border-t border-border-subtle pt-6" />
    </SectionShell>
  );
}

